import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { ArrowLeft, Sparkles, Phone, Mail, CheckCircle2, Building2, Home, Droplet, Leaf } from 'lucide-react';
import { toast } from 'sonner';

const CleaningDemo = () => {
  const { t } = useTranslation();

  const handleQuote = () => {
    toast.success(`✅ ${t('toast.quoteSuccess')}`, {
      description: t('toast.quoteDesc')
    });
  };

  const services = [
    { icon: Building2, name: t('cleaningDemo.service1'), desc: t('cleaningDemo.service1Desc') },
    { icon: Home, name: t('cleaningDemo.service2'), desc: t('cleaningDemo.service2Desc') },
    { icon: Sparkles, name: t('cleaningDemo.service3'), desc: t('cleaningDemo.service3Desc') },
    { icon: Droplet, name: t('cleaningDemo.service4'), desc: t('cleaningDemo.service4Desc') },
  ];

  const features = [
    { name: t('cleaningDemo.feature1'), icon: CheckCircle2 },
    { name: t('cleaningDemo.feature2'), icon: Leaf },
    { name: t('cleaningDemo.feature3'), icon: CheckCircle2 },
    { name: t('cleaningDemo.feature4'), icon: CheckCircle2 },
  ];

  const packages = [
    { name: t('cleaningDemo.packageBasic'), price: '299', features: ['1x wöchentlich', 'Bis 100m²', '2 Stunden'] },
    { name: t('cleaningDemo.packageStandard'), price: '499', features: ['2x wöchentlich', 'Bis 200m²', '4 Stunden'], popular: true },
    { name: t('cleaningDemo.packagePremium'), price: '799', features: ['3x wöchentlich', 'Unbegrenzt', '8 Stunden'] },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-cyan-50">
      {/* Back Link */}
      <div className="fixed top-4 left-4 z-50">
        <Link to="/portfolio">
          <Button variant="outline" className="bg-white/90 hover:bg-white shadow-lg">
            <ArrowLeft size={16} className="mr-2" />
            {t('common.backToPortfolio')}
          </Button>
        </Link>
      </div>

      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-40 bg-white/80 backdrop-blur-md border-b border-blue-100">
        <div className="max-w-7xl mx-auto px-4 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-blue-500 to-cyan-500 flex items-center justify-center">
              <Sparkles className="text-white" size={20} />
            </div>
            <div>
              <span className="font-semibold text-gray-900">{t('cleaningDemo.name')}</span>
              <p className="text-xs text-gray-500">{t('cleaningDemo.tagline')}</p>
            </div>
          </div>
          <Button onClick={handleQuote} className="bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600 text-white">
            <Phone size={16} className="mr-2" />
            {t('cleaningDemo.quoteButton')}
          </Button>
        </div>
      </nav>

      {/* Hero */}
      <section className="relative min-h-[80vh] flex items-center pt-16">
        <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1581578731548-c64695cc6952?w=1920&q=80')] bg-cover bg-center opacity-10" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-32">
          <div className="max-w-2xl">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-blue-100 text-blue-600 text-sm font-medium mb-6">
              <Sparkles size={16} />
              {t('cleaningDemo.tagline')}
            </div>
            <h1 className="text-5xl font-bold text-gray-900 mb-6">
              {t('cleaningDemo.heroTitle')}
            </h1>
            <p className="text-xl text-gray-600 mb-8">
              {t('cleaningDemo.heroDesc')}
            </p>
            <Button onClick={handleQuote} size="lg" className="bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600 text-white">
              {t('cleaningDemo.quoteButton')}
            </Button>
          </div>
        </div>
      </section>

      {/* Services */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Unsere Leistungen</h2>
            <p className="text-gray-600">Professionelle Reinigung für jeden Bedarf</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {services.map((service, i) => (
              <Card key={i} className="border-2 border-blue-100 hover:border-blue-300 transition-all hover:shadow-lg">
                <CardContent className="p-6 text-center">
                  <div className="w-16 h-16 rounded-lg bg-gradient-to-br from-blue-100 to-cyan-100 flex items-center justify-center mx-auto mb-4">
                    <service.icon className="text-blue-600" size={28} />
                  </div>
                  <h3 className="font-semibold text-gray-900 mb-2">{service.name}</h3>
                  <p className="text-sm text-gray-600">{service.desc}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-20 bg-gradient-to-br from-blue-50 to-cyan-50">
        <div className="max-w-5xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900">Warum CleanPro?</h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((feature, i) => (
              <div key={i} className="flex flex-col items-center text-center">
                <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center mb-3">
                  <feature.icon className="text-blue-600" size={24} />
                </div>
                <p className="font-semibold text-gray-900">{feature.name}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Unsere Pakete</h2>
            <p className="text-gray-600">Transparente Preise {t('cleaningDemo.perMonth')}</p>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {packages.map((pkg, i) => (
              <Card key={i} className={`border-2 ${pkg.popular ? 'border-blue-500 shadow-xl scale-105' : 'border-gray-200'}`}>
                {pkg.popular && <div className="bg-blue-500 text-white text-center py-2 text-sm font-semibold">Beliebt</div>}
                <CardContent className="p-8">
                  <h3 className="text-2xl font-bold text-gray-900 mb-2">{pkg.name}</h3>
                  <div className="text-4xl font-bold text-blue-600 mb-6">{pkg.price}€</div>
                  <ul className="space-y-3 mb-8">
                    {pkg.features.map((f, j) => (
                      <li key={j} className="flex items-center gap-2 text-gray-700">
                        <CheckCircle2 className="text-green-500" size={18} />
                        {f}
                      </li>
                    ))}
                  </ul>
                  <Button onClick={handleQuote} className="w-full bg-blue-600 hover:bg-blue-700 text-white">
                    Anfragen
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-gradient-to-r from-blue-600 to-cyan-600">
        <div className="max-w-4xl mx-auto px-4 text-center text-white">
          <h2 className="text-4xl font-bold mb-6">Kostenloses Angebot anfordern</h2>
          <p className="text-xl text-blue-100 mb-8">Kontaktieren Sie uns für ein unverbindliches Angebot</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button onClick={handleQuote} size="lg" className="bg-white text-blue-600 hover:bg-blue-50">
              <Phone size={20} className="mr-2" />
              +49 123 456789
            </Button>
            <Button onClick={handleQuote} size="lg" variant="outline" className="bg-transparent border-2 border-white text-white hover:bg-white/10">
              <Mail size={20} className="mr-2" />
              info@cleanpro.de
            </Button>
          </div>
        </div>
      </section>

      {/* Demo Note */}
      <div className="fixed bottom-4 right-4 bg-white border-2 border-gray-200 rounded-lg p-3 shadow-lg text-sm text-gray-600">
        <p className="font-semibold">{t('common.demoProject')}</p>
      </div>
    </div>
  );
};

export default CleaningDemo;